import React, { useEffect, useState, useRef } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Check, X, Crown, Loader2 } from "lucide-react";

const MAX_ATTEMPTS = 8;
const POLL_MS = 2500;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const nav = useNavigate();
  const [state, setState] = useState({ status: "checking", payment_status: null, message: "Verifying your payment…" });
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error", message: "No session found." });
      return;
    }
    let cancelled = false;
    const poll = async () => {
      attemptsRef.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setState({ status: "success", payment_status: "paid", message: "Payment confirmed", credited: data.credited });
          return;
        }
        if (data.status === "expired") {
          setState({ status: "error", message: "Payment session expired." });
          return;
        }
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setState({ status: "error", message: "Couldn't confirm in time. Check your email or try refreshing." });
          return;
        }
        setTimeout(poll, POLL_MS);
      } catch (e) {
        if (cancelled) return;
        setState({ status: "error", message: e.response?.data?.detail || "Could not verify payment." });
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-neutral-950 text-white flex items-center justify-center p-6">
      <div className="max-w-md w-full border border-neutral-800 p-8 text-center fade-up" data-testid="payment-result">
        {state.status === "checking" && (
          <>
            <Loader2 className="w-12 h-12 text-blue-500 mx-auto mb-4 animate-spin" />
            <div className="font-display text-3xl uppercase">Confirming…</div>
            <div className="text-sm text-neutral-400 mt-2">{state.message}</div>
          </>
        )}
        {state.status === "success" && (
          <>
            <Crown className="w-12 h-12 text-blue-500 mx-auto mb-4" />
            <div className="text-xs tracking-[0.3em] uppercase text-blue-500 font-bold">WELCOME TO PRO</div>
            <div className="font-display text-4xl uppercase mt-2">All Unlocked</div>
            <div className="text-sm text-neutral-400 mt-3">Thanks for fueling the grind 💪</div>
            <Button data-testid="goto-dashboard-btn" onClick={() => nav("/dashboard")} className="w-full bg-blue-500 hover:bg-blue-600 rounded-none uppercase tracking-wider font-display h-10 mt-6">
              <Check className="w-4 h-4 mr-2" /> Open Dashboard
            </Button>
          </>
        )}
        {state.status === "error" && (
          <>
            <X className="w-12 h-12 text-neutral-500 mx-auto mb-4" />
            <div className="font-display text-3xl uppercase">Something Off</div>
            <div className="text-sm text-neutral-400 mt-2">{state.message}</div>
            <div className="flex gap-2 mt-6">
              <Link to="/pricing" className="flex-1"><Button variant="outline" className="w-full bg-transparent border-neutral-700 text-white rounded-none uppercase tracking-wider font-display h-10">Try Again</Button></Link>
              <Link to="/dashboard" className="flex-1"><Button className="w-full bg-blue-500 hover:bg-blue-600 rounded-none uppercase tracking-wider font-display h-10">Dashboard</Button></Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
